import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, from } from 'rxjs';
import { tap, shareReplay, finalize, switchMap } from 'rxjs/operators';
import { AuthCustomService } from '../../security/auth-custom.service';
import { UserRef } from '../../core/generated/model/userRef';
import { UserRefControllerService } from '../../core/generated/api/userRefController.service';

@Injectable({
  providedIn: 'root',
})
export class UserPreferencesCacheService {
  private usersService = inject(UserRefControllerService);
  private authCustomService = inject(AuthCustomService);

  private userSubject = new BehaviorSubject<UserRef>({ id: 0, email: '' });

  // Separate loaded flags so loading user doesn't interfere with other state
  private loadedUser = false;

  // In-flight request observable to prevent duplicate backend calls when multiple callers subscribe concurrently
  private userRequest$: Observable<UserRef> | null = null;

  // Carregar usuário logado
  getUser(): Observable<UserRef> {
    if (this.authCustomService.hasValidToken()) {
      const tokenEmail = (this.authCustomService.userEmail || '').toLowerCase();

      // If user already loaded, return the cached observable
      if (this.loadedUser && this.userSubject.value && this.userSubject.value.id !== 0) {
        const cachedEmail = (this.userSubject.value.email || '').toLowerCase();
        // If account changed, invalidate in-memory cache to force backend refresh.
        if (tokenEmail && cachedEmail && tokenEmail !== cachedEmail) {
          this.loadedUser = false;
          this.userRequest$ = null;
          this.userSubject.next({ id: 0, email: '' });
        } else {
          return this.userSubject.asObservable();
        }
      }

      if (this.loadedUser && this.userSubject.value && this.userSubject.value.id !== 0) {
        return this.userSubject.asObservable();
      }

      if (this.userRequest$) {
        return this.userRequest$;
      }

      this.userRequest$ = this.usersService.getLoggedUser().pipe(
        switchMap((response: any) => {
          // Se a resposta for um Blob, converter para JSON
          if (response instanceof Blob) {
            return from(response.text()).pipe(
              switchMap(text => of(JSON.parse(text) as UserRef))
            );
          }
          return of(response as UserRef);
        }),
        tap((user) => {
          this.userSubject.next(user);
          this.loadedUser = true;
        }),
        shareReplay(1),
        finalize(() => {
          this.userRequest$ = null;
        })
      );

      return this.userRequest$;
    }
    this.clearUserCache();
    return of({ id: 0, email: '' });
  }

  clearUserCache(): void {
    this.loadedUser = false;
    this.userRequest$ = null;
    this.userSubject.next({ id: 0, email: '' });
  }

  // Atualizar usuário
  updateUser(userId: string, user: UserRef): Observable<UserRef> {
    return this.usersService.update(Number(userId), user).pipe(
      tap((updatedUser) => this.userSubject.next(updatedUser))
    );
  }
}
